import React, { useState, useEffect } from 'react';
import './UserSubscriptionModal.css';

const UserSubscriptionModal = ({ user, isOpen, onClose, onUpdatePremium, updatingPremium }) => {
    const [premiumType, setPremiumType] = useState(user?.premium_type || 'monthly');
    const [premiumDays, setPremiumDays] = useState(30);
    const [activeTab, setActiveTab] = useState('overview');

    useEffect(() => {
        if (user) {
            setPremiumType(user.premium_type || 'monthly');
        }
    }, [user]);

    if (!isOpen || !user) return null;

    const formatDate = (date) => {
        if (!date) return 'N/A';
        if (date.toDate) {
            return date.toDate().toLocaleDateString('en-US', { 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
            });
        }
        return date;
    };

    const getNameInitials = (firstName, lastName) => {
        const first = firstName?.charAt(0)?.toUpperCase() || '';
        const last = lastName?.charAt(0)?.toUpperCase() || '';
        return first + last || 'U';
    };

    const isExpired = user.premium_expiry && user.premium_expiry.toDate && user.premium_expiry.toDate() < new Date();

    return (
        <div className="user-subscription-modal-overlay" onClick={onClose}>
            <div className="user-subscription-modal-content" onClick={(e) => e.stopPropagation()}>
                {/* Header */}
                <div className="user-modal-header">
                    <div className="user-modal-header-left">
                        <div className="user-modal-avatar">
                            {getNameInitials(user.firstName, user.lastName)}
                        </div>
                        <div className="user-modal-header-info">
                            <h2>{user.fullName || `${user.firstName || ''} ${user.lastName || ''}`.trim() || 'Unknown User'}</h2>
                            <p className="user-modal-email">{user.email}</p>
                        </div>
                    </div>
                    <button className="user-modal-close" onClick={onClose} aria-label="Close">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                            <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                        </svg>
                    </button>
                </div>

                {/* Tabs */}
                <div className="user-modal-tabs">
                    <button 
                        className={`user-modal-tab ${activeTab === 'overview' ? 'active' : ''}`}
                        onClick={() => setActiveTab('overview')}
                    >
                        Overview
                    </button>
                    <button 
                        className={`user-modal-tab ${activeTab === 'subscription' ? 'active' : ''}`}
                        onClick={() => setActiveTab('subscription')}
                    >
                        Subscription
                    </button>
                    <button 
                        className={`user-modal-tab ${activeTab === 'details' ? 'active' : ''}`}
                        onClick={() => setActiveTab('details')}
                    >
                        Details
                    </button>
                </div>

                {/* Content */}
                <div className="user-modal-body">
                    {activeTab === 'overview' && (
                        <div className="user-modal-overview">
                            <div className="user-stat-cards">
                                <div className="user-stat-card">
                                    <div className="user-stat-icon premium">
                                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                                            <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" fill="currentColor"/>
                                        </svg>
                                    </div>
                                    <div className="user-stat-content">
                                        <div className="user-stat-label">Premium Status</div>
                                        <div className={`user-stat-value ${user.premium ? (isExpired ? 'expired' : 'active') : 'inactive'}`}>
                                            {user.premium ? (isExpired ? 'Expired' : 'Active') : 'Inactive'}
                                        </div>
                                    </div>
                                </div>
                                <div className="user-stat-card">
                                    <div className="user-stat-icon type">
                                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                                            <path d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM13 17H11V15H13V17ZM13 13H11V7H13V13Z" fill="currentColor"/>
                                        </svg>
                                    </div>
                                    <div className="user-stat-content">
                                        <div className="user-stat-label">Subscription Type</div>
                                        <div className="user-stat-value">{user.premium_type || 'None'}</div>
                                    </div>
                                </div>
                                <div className="user-stat-card">
                                    <div className="user-stat-icon expiry">
                                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                                            <path d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM13 13H11V7H13V13ZM13 17H11V15H13V17Z" fill="currentColor"/>
                                        </svg>
                                    </div>
                                    <div className="user-stat-content">
                                        <div className="user-stat-label">Expiry Date</div>
                                        <div className="user-stat-value">
                                            {user.premium_expiry 
                                                ? formatDate(user.premium_expiry)
                                                : user.premium_type === 'lifetime' || user.premium_type === 'whitelist' ? 'Never' : 'N/A'}
                                        </div>
                                    </div>
                                </div>
                                <div className="user-stat-card">
                                    <div className="user-stat-icon source">
                                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                                            <path d="M12 2L2 7L12 12L22 7L12 2ZM2 17L12 22L22 17M2 12L12 17L22 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                        </svg>
                                    </div>
                                    <div className="user-stat-content">
                                        <div className="user-stat-label">Source</div>
                                        <div className="user-stat-value">{user.premium_source || 'None'}</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {activeTab === 'subscription' && (
                        <div className="user-modal-subscription">
                            <div className="user-subscription-current">
                                <h3>Current Subscription</h3>
                                <div className="user-subscription-info-grid">
                                    <div className="user-info-item">
                                        <span className="user-info-label">Status</span>
                                        <span className={`user-info-value status ${user.premium ? (isExpired ? 'expired' : 'active') : 'inactive'}`}>
                                            {user.premium ? (isExpired ? 'Expired' : 'Active') : 'Inactive'}
                                        </span>
                                    </div>
                                    <div className="user-info-item">
                                        <span className="user-info-label">Type</span>
                                        <span className="user-info-value">{user.premium_type || 'None'}</span>
                                    </div>
                                    <div className="user-info-item">
                                        <span className="user-info-label">Expiry</span>
                                        <span className="user-info-value">
                                            {user.premium_expiry 
                                                ? formatDate(user.premium_expiry)
                                                : user.premium_type === 'lifetime' || user.premium_type === 'whitelist' ? 'Never' : 'N/A'}
                                        </span>
                                    </div>
                                    <div className="user-info-item">
                                        <span className="user-info-label">Source</span>
                                        <span className="user-info-value">{user.premium_source || 'None'}</span>
                                    </div>
                                </div>
                            </div>

                            <div className="user-subscription-update">
                                <h3>Update Subscription</h3>
                                <div className="user-form-section">
                                    <label className="user-form-label">Premium Type</label>
                                    <select
                                        className="user-form-select"
                                        value={premiumType}
                                        onChange={(e) => setPremiumType(e.target.value)}
                                    >
                                        <option value="monthly">Monthly</option>
                                        <option value="yearly">Yearly</option>
                                        <option value="lifetime">Lifetime</option>
                                        <option value="whitelist">Whitelist</option>
                                    </select>
                                </div>
                                {premiumType !== 'lifetime' && premiumType !== 'whitelist' && (
                                    <div className="user-form-section">
                                        <label className="user-form-label">Duration (days)</label>
                                        <input
                                            type="number"
                                            className="user-form-input"
                                            value={premiumDays}
                                            onChange={(e) => setPremiumDays(parseInt(e.target.value) || 30)}
                                            min="1"
                                        />
                                    </div>
                                )}
                                <div className="user-modal-actions">
                                    <button
                                        className="user-action-btn user-action-btn-primary"
                                        onClick={() => onUpdatePremium(true, premiumType, premiumDays)}
                                        disabled={updatingPremium}
                                    >
                                        {updatingPremium ? (
                                            <>
                                                <div className="user-spinner-small"></div>
                                                <span>Updating...</span>
                                            </>
                                        ) : (
                                            <>
                                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                                                    <path d="M20 6L9 17L4 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                                </svg>
                                                <span>Enable Premium</span>
                                            </>
                                        )}
                                    </button>
                                    <button
                                        className="user-action-btn user-action-btn-danger"
                                        onClick={() => onUpdatePremium(false, null, null)}
                                        disabled={updatingPremium}
                                    >
                                        {updatingPremium ? (
                                            <>
                                                <div className="user-spinner-small"></div>
                                                <span>Updating...</span>
                                            </>
                                        ) : (
                                            <>
                                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                                                    <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                                                </svg>
                                                <span>Disable Premium</span>
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {activeTab === 'details' && (
                        <div className="user-modal-details">
                            <div className="user-details-grid">
                                <div className="user-detail-section">
                                    <h4>Personal Information</h4>
                                    <div className="user-detail-items">
                                        <div className="user-detail-row">
                                            <span className="user-detail-label">Full Name</span>
                                            <span className="user-detail-value">{user.fullName || `${user.firstName || ''} ${user.lastName || ''}`.trim() || 'N/A'}</span>
                                        </div>
                                        <div className="user-detail-row">
                                            <span className="user-detail-label">Email</span>
                                            <span className="user-detail-value">{user.email || 'N/A'}</span>
                                        </div>
                                        <div className="user-detail-row">
                                            <span className="user-detail-label">User ID</span>
                                            <span className="user-detail-value user-id">{user.id}</span>
                                        </div>
                                        <div className="user-detail-row">
                                            <span className="user-detail-label">Gender</span>
                                            <span className="user-detail-value">{user.gender || 'N/A'}</span>
                                        </div>
                                        <div className="user-detail-row">
                                            <span className="user-detail-label">Date of Birth</span>
                                            <span className="user-detail-value">{user.dob || 'N/A'}</span>
                                        </div>
                                    </div>
                                </div>
                                <div className="user-detail-section">
                                    <h4>Contact Information</h4>
                                    <div className="user-detail-items">
                                        <div className="user-detail-row">
                                            <span className="user-detail-label">Phone</span>
                                            <span className="user-detail-value">
                                                {user.countryCode && user.phone
                                                    ? `${user.countryCode} ${user.phone}`
                                                    : user.phone || 'N/A'}
                                            </span>
                                        </div>
                                        <div className="user-detail-row">
                                            <span className="user-detail-label">Country</span>
                                            <span className="user-detail-value">{user.country || 'N/A'}</span>
                                        </div>
                                    </div>
                                </div>
                                <div className="user-detail-section">
                                    <h4>Account Information</h4>
                                    <div className="user-detail-items">
                                        <div className="user-detail-row">
                                            <span className="user-detail-label">Created</span>
                                            <span className="user-detail-value">
                                                {user.createdAt ? formatDate(user.createdAt) : 'N/A'}
                                            </span>
                                        </div>
                                        <div className="user-detail-row">
                                            <span className="user-detail-label">Last Login</span>
                                            <span className="user-detail-value">
                                                {user.lastLogin ? formatDate(user.lastLogin) : 'N/A'}
                                            </span>
                                        </div>
                                        <div className="user-detail-row">
                                            <span className="user-detail-label">Email Verified</span>
                                            <span className="user-detail-value">
                                                {user.emailVerified ? 'Yes' : 'No'}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default UserSubscriptionModal;

